/**
 * SPQR Animations — GSAP ScrollTrigger foundation
 * Scroll-triggered reveal animations for homepage sections.
 * Loaded conditionally via settings.animations_gsap_enable.
 *
 * Architecture:
 * - gsap.context() per section for scoped cleanup
 * - gsap.matchMedia() for responsive + reduced-motion
 * - Shopify section lifecycle events for theme editor compat
 * - Only GPU-accelerated properties (transform, opacity)
 */

(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  // ─── Config ─────────────────────────────────────────────
  var config = (window.theme && window.theme.animations) || {};
  if (config.enabled === false) return;

  var intensity = config.intensity || 'moderate';
  var speed = config.speed || 1;

  var PRESETS = {
    subtle:   { y: 20,  scale: 0.98, stagger: 0.06, duration: 0.6 },
    moderate: { y: 40,  scale: 0.95, stagger: 0.1,  duration: 0.8 },
    dramatic: { y: 60,  scale: 0.9,  stagger: 0.14, duration: 1.0 }
  };
  var P = PRESETS[intensity] || PRESETS.moderate;

  var TOGGLE_PLAY = 'play none none none';
  var EASE = 'power2.out';

  function dur(base) {
    return (base || P.duration) / speed;
  }

  // ─── Shared scroll-reveal helper ────────────────────────
  // Reduces per-section boilerplate. Uses autoAlpha (visibility + opacity)
  // to prevent FOUC: elements start visibility:hidden until animated.
  function reveal(els, container, opts) {
    if (!els || !els.length) return;
    var defaults = {
      autoAlpha: 0,
      duration: dur(opts.duration),
      ease: opts.ease || EASE,
      stagger: opts.stagger || P.stagger
    };
    if (opts.y) defaults.y = opts.y;
    if (opts.scale) defaults.scale = opts.scale;
    if (opts.delay) defaults.delay = opts.delay / speed;
    if (container) {
      defaults.scrollTrigger = {
        trigger: container,
        start: opts.start || 'top 85%',
        toggleActions: TOGGLE_PLAY
      };
    }
    gsap.from(els, defaults);
  }

  // ─── Section context registry ───────────────────────────
  var sectionContexts = new Map();

  // ─── Animation definitions per section type ─────────────
  var SECTION_ANIMATIONS = {

    'slideshow-section': function (container) {
      reveal(
        container.querySelectorAll('.hero__title, .slideshow__title'),
        null, { y: P.y, duration: 1, ease: 'power3.out' }
      );
      reveal(
        container.querySelectorAll('.hero__subtitle, .slideshow__subtitle, .hero__link'),
        null, { y: P.y * 0.5, duration: 0.8, delay: 0.3 }
      );
    },

    'featured-collections': function (container) {
      reveal(
        container.querySelectorAll('.grid__item, .collection-item'),
        container, { y: P.y }
      );
    },

    'lifestyle-grid': function (container) {
      reveal(
        container.querySelectorAll('.lifestyle-grid__image-wrapper'),
        container, { scale: P.scale, duration: 1 }
      );
      reveal(
        container.querySelectorAll('.lifestyle-grid__content'),
        container, { y: P.y * 0.6, duration: 0.8, start: 'top 75%' }
      );
    },

    'collection-showcase': function (container) {
      reveal(
        container.querySelectorAll('.collection-showcase__heading'),
        container, { y: P.y * 0.6 }
      );
      reveal(
        container.querySelectorAll('.collection-showcase__image-wrapper, .collection-showcase__image'),
        container, { scale: P.scale, duration: 1, start: 'top 80%' }
      );
      reveal(
        container.querySelectorAll('.collection-showcase__product, .collection-showcase__slide'),
        container, { y: P.y * 0.5, duration: 0.7, start: 'top 75%' }
      );
    },

    'collection-showcase-slider': function (container) {
      reveal(
        container.querySelectorAll('.section-header__title'),
        container, { y: P.y * 0.5 }
      );
      reveal(
        container.querySelectorAll('.collection-showcase-slider__slide'),
        container, { y: P.y * 0.4, duration: 0.7, stagger: P.stagger * 0.8, start: 'top 80%' }
      );
    },

    'testimonials-masonry': function (container) {
      reveal(
        container.querySelectorAll('.testimonial-card, .masonry-item'),
        container, { y: P.y }
      );
    },

    'rich-text-section': function (container) {
      reveal(
        container.querySelectorAll('.rich-text__heading, .rich-text__text, .rte'),
        container, { y: P.y * 0.6, stagger: P.stagger * 0.7 }
      );
    },

    'text-columns': function (container) {
      reveal(
        container.querySelectorAll('.text-columns__item, .grid__item'),
        container, { y: P.y }
      );
    },

    'contact-form': function (container) {
      reveal(
        container.querySelectorAll('.section-header__title'),
        container, { y: P.y * 0.5 }
      );
      reveal(
        container.querySelectorAll('form'),
        container, { y: P.y * 0.3, duration: 0.7, delay: 0.15, start: 'top 80%' }
      );
    }
  };

  // ─── Initialize a section ───────────────────────────────
  function initSection(sectionEl) {
    var sectionType = sectionEl.getAttribute('data-section-type');
    var sectionId = sectionEl.getAttribute('data-section-id');
    if (!sectionType || !sectionId) return;

    var animFn = SECTION_ANIMATIONS[sectionType];
    if (!animFn) return;

    if (sectionContexts.has(sectionId)) {
      sectionContexts.get(sectionId).revert();
    }

    var ctx = gsap.context(function () {
      animFn(sectionEl);
    }, sectionEl);

    sectionContexts.set(sectionId, ctx);
  }

  // ─── Main init with matchMedia ──────────────────────────
  function init() {
    var mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: no-preference)', function () {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        gsap.globalTimeline.timeScale(3);
      }

      gsap.utils.toArray('[data-section-type]:not([data-section-type="header"])').forEach(initSection);
    });

    // Reduced motion: opacity-only fades, no movement
    mm.add('(prefers-reduced-motion: reduce)', function () {
      gsap.utils.toArray('[data-section-type]:not([data-section-type="header"])').forEach(function (sectionEl) {
        var sectionId = sectionEl.getAttribute('data-section-id');
        if (!sectionId) return;

        var ctx = gsap.context(function () {
          var children = sectionEl.querySelectorAll(':scope > * > *');
          if (children.length) {
            gsap.from(children, {
              autoAlpha: 0,
              duration: 0.35,
              ease: 'power1.out',
              stagger: 0.04,
              scrollTrigger: {
                trigger: sectionEl,
                start: 'top 90%',
                toggleActions: TOGGLE_PLAY
              }
            });
          }
        }, sectionEl);

        sectionContexts.set(sectionId, ctx);
      });
    });
  }

  // ─── Shopify section lifecycle ──────────────────────────
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-section-type]')
      || (event.target.hasAttribute('data-section-type') ? event.target : null);
    if (section) initSection(section);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var sectionId = event.detail.sectionId;
    if (sectionContexts.has(sectionId)) {
      sectionContexts.get(sectionId).revert();
      sectionContexts.delete(sectionId);
    }
  });

  document.addEventListener('shopify:section:reorder', function () {
    sectionContexts.forEach(function (ctx) { ctx.revert(); });
    sectionContexts.clear();
    gsap.utils.toArray('[data-section-type]:not([data-section-type="header"])').forEach(initSection);
    ScrollTrigger.refresh();
  });

  // ─── Start ──────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
